import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";

import {
  parseNumericValue,
  calculateMonthlyRate,
  calculateTimePeriod,
  roundToPrecision
} from "../utils/calculations";

import { generateYearlyResults } from "../utils/data-generators";
import { MAX_MONTHS } from "../utils/constants";

const calculatorKey = "primeiro-milhao";
const TARGET_AMOUNT = 1000000;

export const PrimeiroMilhaoInputCard = ({
  setResults,
}: {
  setResults: (value: PrimeiroMilhaoResultsType | null) => void;
}) => {
  const isVerified = useIsVerified();
  const [message, setMessage] = useState<string>("");

  const [calculationMode, setCalculationMode] = useState<"prazo" | "aporte">("prazo");
  const [initialValue, setInitialValue] = useState("");
  const [monthlyValue, setMonthlyValue] = useState("");
  const [targetPeriod, setTargetPeriod] = useState("1");
  const [interestRate, setInterestRate] = useState("8");
  const [interestPeriodType, setInterestPeriodType] = useState("anual");
  const [periodType, setPeriodType] = useState("ano(s)");

  function clear() {
    setInitialValue("");
    setMonthlyValue("");
    setTargetPeriod("1");
    setInterestRate("8");
    setInterestPeriodType("anual");
    setPeriodType("ano(s)");
    setCalculationMode("prazo");
    setResults(null);
    setMessage("");
  }

  function calcularPrimeiroMilhao() {
    if (!initialValue && !monthlyValue) {
      setMessage("Por favor adicione um valor inicial ou mensal.");
      return;
    }

    if (!interestRate) {
      setMessage("Adicione taxa de juros.");
      return;
    }

    if (calculationMode === "aporte" && !targetPeriod) {
      setMessage("Adicione o período.");
      return;
    }

    if (calculationMode === "prazo" && !monthlyValue) {
      setMessage("Adicione valor mensal.");
      return;
    }

    let start = parseNumericValue(initialValue || "0");
    const rate = calculateMonthlyRate(interestRate, interestPeriodType);

    if (isNaN(start) || start < 0) {
      setMessage("Valor inicial deve ser positivo.");
      return;
    }

    if (start >= TARGET_AMOUNT) {
      setMessage("Parabéns! Você já atingiu a meta de R$ 1 milhão!");
      return;
    }

    setMessage("");

    if (calculationMode === "prazo") {
      // Calcular tempo necessário
      const monthlyAporte = parseNumericValue(monthlyValue || "0");
      
      if (monthlyAporte === 0 && rate <= 0) {
        setMessage("Sem aportes mensais e sem rendimento, não é possível atingir a meta.");
        return;
      }

      let accumulated = start;
      let totalInvested = start;
      let totalInterest = 0;
      let months = 0;

      const monthlyResult: number[] = [];
      const monthlyInvested: number[] = [start];
      const monthlyTax: number[] = [];
      const monthlyAcumulatedInterest: number[] = [];

      while (accumulated < TARGET_AMOUNT && months < MAX_MONTHS) {
        const monthlyInterest = accumulated * rate;
        totalInterest += monthlyInterest;
        
        accumulated = roundToPrecision(accumulated + monthlyInterest + monthlyAporte);
        totalInvested = roundToPrecision(totalInvested + monthlyAporte);
        months++;

        monthlyTax.push(roundToPrecision(monthlyInterest));
        monthlyResult.push(roundToPrecision(totalInterest));
        monthlyInvested.push(roundToPrecision(totalInvested));
        monthlyAcumulatedInterest.push(roundToPrecision(accumulated));
      }

      if (months >= MAX_MONTHS) {
        setMessage("Meta não atingida no período máximo analisado (100 anos).");
        return;
      }

      const timeToGoal = calculateTimePeriod(months);
      const yearlyResults = generateYearlyResults(start, monthlyAporte, rate, months);

      setResults({
        accumulated: roundToPrecision(accumulated),
        invested: roundToPrecision(totalInvested),
        interest: roundToPrecision(totalInterest),
        monthlyResult,
        monthlyInvested,
        monthlyTax,
        monthlyAcumulatedInterest,
        goalReached: accumulated >= TARGET_AMOUNT,
        calculationMode: "prazo",
        timeToGoal,
        targetAmount: TARGET_AMOUNT, // 🚨 CORRIGIDO: Adicionado targetAmount
        yearlyResults
      });

    } else {
      // Calcular aporte mensal necessário
      const months = parseNumericValue(targetPeriod) * (periodType === "ano(s)" ? 12 : 1);
      
      if (months <= 0 || months > MAX_MONTHS) {
        setMessage("Período inválido.");
        return;
      }

      let monthlyPayment: number;
      
      if (rate === 0) {
        monthlyPayment = (TARGET_AMOUNT - start) / months;
      } else {
        const futureValueOfInitial = start * Math.pow(1 + rate, months);
        const denominator = ((Math.pow(1 + rate, months) - 1) / rate);
        monthlyPayment = (TARGET_AMOUNT - futureValueOfInitial) / denominator;
      }

      if (monthlyPayment <= 0) {
        setMessage("Com o valor inicial informado, você já atingiria a meta!");
        return;
      }

      if (monthlyPayment > 500000) {
        setMessage("Aporte mensal necessário muito alto. Considere aumentar o prazo.");
        return;
      }

      const timeToGoal = calculateTimePeriod(months);
      const totalInvested = start + (monthlyPayment * months);
      const totalInterest = TARGET_AMOUNT - totalInvested;
      const yearlyResults = generateYearlyResults(start, monthlyPayment, rate, months);

      const monthlyResult: number[] = [];
      const monthlyInvested: number[] = [start];
      const monthlyTax: number[] = [];
      const monthlyAcumulatedInterest: number[] = [];

      let tempAccumulated = start;
      let tempInvested = start;
      let tempTotalInterest = 0;

      for (let i = 0; i < months; i++) {
        const monthlyInterest = tempAccumulated * rate;
        tempTotalInterest += monthlyInterest;
        
        tempAccumulated = roundToPrecision(tempAccumulated + monthlyInterest + monthlyPayment);
        tempInvested = roundToPrecision(tempInvested + monthlyPayment);

        monthlyTax.push(roundToPrecision(monthlyInterest));
        monthlyResult.push(roundToPrecision(tempTotalInterest));
        monthlyInvested.push(roundToPrecision(tempInvested));
        monthlyAcumulatedInterest.push(roundToPrecision(tempAccumulated));
      }

      setResults({
        accumulated: TARGET_AMOUNT,
        invested: roundToPrecision(totalInvested),
        interest: roundToPrecision(totalInterest),
        monthlyResult,
        monthlyInvested,
        monthlyTax,
        monthlyAcumulatedInterest,
        goalReached: true,
        calculationMode: "aporte",
        timeToGoal,
        targetAmount: TARGET_AMOUNT, // 🚨 CORRIGIDO: Adicionado targetAmount
        monthlyPaymentNeeded: roundToPrecision(monthlyPayment),
        yearlyResults
      });
    }
  }

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      calculationMode,
      initialValue,
      monthlyValue,
      targetPeriod,
      interestRate,
      interestPeriodType,
      periodType,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    
    setCalculationMode((savedData.calculationMode as "prazo" | "aporte") || "prazo");
    setInitialValue((savedData.initialValue as string) || "");
    setMonthlyValue((savedData.monthlyValue as string) || "");
    setTargetPeriod((savedData.targetPeriod as string) || "1");
    setInterestRate((savedData.interestRate as string) || "8");
    setInterestPeriodType((savedData.interestPeriodType as string) || "anual");
    setPeriodType((savedData.periodType as string) || "ano(s)");
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Calculadora do Primeiro Milhão</MainCardTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
          checkCalculatorUsageLimitAndRedirect(calcularPrimeiroMilhao);
        }}
      >
        <MainCardContent>
          {/* Tipo de Cálculo */}
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="calculation-mode">
              Tipo de Cálculo
            </label>
            <select
              id="calculation-mode"
              className="w-full p-2 border"
              value={calculationMode}
              onChange={(event) => setCalculationMode(event.target.value as "prazo" | "aporte")}
            >
              <option value="prazo">Calcular prazo para atingir R$ 1 milhão</option>
              <option value="aporte">Calcular aporte mensal necessário</option>
            </select>
          </div>

          {/* Valor Inicial */}
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="initial-value">
              Valor inicial
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="initial-value"
                placeholder="00,00"
                type="text"
                value={initialValue}
                onChange={(event) => setInitialValue(sanitizeBrlValue(event))}
              />
            </div>
          </div>

          {/* Valor Mensal - apenas no modo prazo */}
          {calculationMode === "prazo" && (
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="monthly-value">
                Valor mensal
              </label>
              <div className="flex border">
                <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                  R$
                </span>
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="monthly-value"
                  placeholder="00,00"
                  type="text"
                  value={monthlyValue}
                  onChange={(event) => setMonthlyValue(sanitizeBrlValue(event))}
                />
              </div>
            </div>
          )}

          {/* Taxa de Juros */}
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="interest-rate">
              Taxa de juros
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                %
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="interest-rate"
                placeholder="8,00"
                type="text"
                value={interestRate}
                onChange={(event) => {
                  const value = event.target.value.replace(/[^0-9,]/g, "");
                  setInterestRate(value);
                }}
              />
              <select
                className="border-l p-2 bg-white"
                value={interestPeriodType}
                onChange={(event) => setInterestPeriodType(event.target.value)}
              >
                <option>anual</option>
                <option>mensal</option>
              </select>
            </div>
          </div>

          {calculationMode === "aporte" && (
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="target-period">
                Período
              </label>
              <div className="flex border">
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="target-period"
                  placeholder="1"
                  type="text"
                  value={targetPeriod}
                  onChange={(event) => setTargetPeriod(event.target.value)}
                />
                <select
                  className="border-l p-2 bg-white"
                  value={periodType}
                  onChange={(event) => setPeriodType(event.target.value)}
                >
                  <option>ano(s)</option>
                  <option>mes(es)</option>
                </select>
              </div>
            </div>
          )}
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified}
            type="submit"
            variant={"default"}
            className="sm:w-[150px]"
          >
            Calcular
          </Button>

          <div className="flex flex-col sm:flex-row items-center gap-2">
            <Button onClick={clear} variant={"ghost"}>
              Limpar
            </Button>
          </div>
        </div>
      </form>
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};